<script>
    import {_, isLoading, locale, locales} from 'svelte-i18n';
    import {onMount, setContext} from 'svelte';
    import Fa from 'svelte-fa';
    import Checkout from './lib/Checkout.svelte';
    import SystemMessage from './lib/SystemMessage.svelte';
    import {faGlobe, faLock, faUser, faWallet, faBars, faXmark} from '@fortawesome/free-solid-svg-icons';
    import {logEvent, setUserProperties,} from 'firebase/analytics';
    import Loader from './lib/Loader.svelte';
    import {getCurrentTheme} from './themeLoader.js';
    import VanishingHeader from './lib/VanishingHeader.svelte';
    import {
        getStorePacks,
        getConfig,
        selectedCurrency,
        storePacks,
        storeSection,
        systemMessages,
        rechargeRewardsEvent,
        adventCalendarEvent,
        user,
        castleLevel, luckyDrawEvent, vipLevel, maxWebshopDiscount, gameConfig,
        navigateToSection, growthFundConfig, monsterGrowthFundConfig,
        claimedGrowthFundPrizes, claimedMonsterGrowthFundPrizes,
        storePacksError,
        storePacksLoading,
        battlePassEvent,
        playerGrowthFund, playerMonsterGrowthFund, defeatedMonsterLevels,
        userMenuOpen,
        chainPromoEvent
    } from './stores';
    import Modal from './lib/Modal.svelte';
    import Promos from './lib/views/Promos.svelte';
    import DailyDeals from './lib/views/DailyDeals.svelte';
    import Subscriptions from './lib/views/Subscriptions.svelte';
    import Settings from "./lib/Settings.svelte";
    import GoldRush from "./lib/views/GoldRush.svelte";
    import Gold from './lib/views/Gold.svelte';
    import {logout_route} from './constants';
    import {analytics, logMetric} from './metrics';
    import Resources from "./lib/views/Resources.svelte";
    import {subscriptions} from "./stores.js";
    import RedeemCode from "./lib/views/RedeemCode.svelte";
    import GiftFriend from "./lib/views/GiftFriend.svelte";
    import Ribbon from "./lib/Ribbon.svelte";
    import LuckyDraw from "./lib/views/LuckyDraw.svelte";
    import BeginnerPacks from "./lib/views/BeginnerPacks.svelte";
    import WelcomeBackPacks from "./lib/views/WelcomeBackPacks.svelte";
    import MidweekMadness from "./lib/views/MidweekMadness.svelte";
    import Vip from "./lib/views/Vip.svelte";
    import ResourceTrove from "./lib/views/ResourceTrove.svelte";
    import GrowthCapital from "./lib/views/GrowthCapital.svelte";
    import PartisanProgress from "./lib/views/PartisanProgress.svelte";
    import ScrollableNavbar from "./lib/ScrollableNavbar.svelte";
    import MobileTabBar from "./lib/MobileTabBar.svelte";
    import NotificationDot from "./lib/NotificationDot.svelte";
    import {deriveTabInterest} from "./lib/tab-interest.js";
    import {markSeenFlag} from "./lib/tab-seen.js";
    import { dragScroll } from "./lib/actions/dragScroll.js";
    import { isMobile } from "./stores.js";
    import Featured from "./lib/views/Featured.svelte";
    import WebshopExclusive from "./lib/views/WebshopExclusive.svelte";
    import Notifications from "./lib/Notifications.svelte";
    import AdventCalendar from "./lib/views/AdventCalendar.svelte";
    import FreeGifts from "./lib/views/FreeGifts.svelte";
    import CustomizablePacks from "./lib/views/CustomizablePacks.svelte";
    import SpecialEvents from "./lib/views/SpecialEvents.svelte";
    import CardCollection from "./lib/views/CardCollection.svelte";
    import BattlePass from "./lib/views/BattlePass.svelte";
    import DividedNation from "./lib/views/DividedNation.svelte";
    import ChainDeals from "./lib/views/ChainDeals.svelte";
    import SingleJackFever from "./lib/views/SingleJackFever.svelte";
    import WebBattlePass from "./lib/battlepass/WebBattlePass.svelte";
    import StoreError from "./lib/StoreError.svelte";
    import {goTo} from './utils';
    import GravityMalfunction from "./lib/GravityMalfunction.svelte";

    let { params = {} } = $props();

    let _user = $state(null);
    let _systemMessages = $state(null);
    let getPacks = $state(null);

    // Theme support
    const currentTheme = getCurrentTheme();
    const iconColor = currentTheme === 'black_friday' ? '#f12626' : currentTheme === 'winter_theme' ? '#96e3ff' : 'gold';
    const discountBannerClass = currentTheme === 'black_friday' ? 'bf' : currentTheme === 'winter_theme' ? 'winter' : '';

    let section = $derived(params.section || 'featured');

    // Keep storeSection in sync when the URL-derived section changes (e.g., browser back/forward)
    // Only set if the value actually changed to prevent infinite loops with redirect logic
    $effect(() => {
        if (section !== $storeSection) {
            storeSection.set(section);
        }
    });

    // Red dots on the submenu sub-tabs that have a claimable "action" waiting
    // (Subscriptions / Resources with an uncollected reward, Growth Capital /
    // Partisan Progress with a claimable prize). The event sub-tabs use their own
    // NEW ribbon, so they aren't dotted here. These are pure derived state, so they
    // clear automatically once the underlying claim updates the store.
    let subTabInterest = $derived(deriveTabInterest({
        subscriptions: $subscriptions,
        growthFundConfig: $growthFundConfig,
        claimedGrowthFundPrizes: $claimedGrowthFundPrizes,
        playerGrowthFund: $playerGrowthFund,
        castleLevel: $castleLevel,
        monsterGrowthFundConfig: $monsterGrowthFundConfig,
        claimedMonsterGrowthFundPrizes: $claimedMonsterGrowthFundPrizes,
        playerMonsterGrowthFund: $playerMonsterGrowthFund,
        defeatedMonsterLevels: $defeatedMonsterLevels,
    }));

    user.subscribe((data) => {
        if (data === null) return;

        setUserProperties(analytics, {
            username: data.user.username,
            id: data.user.id,
        });

        _user = data;
        getPacks = getStorePacks();
        getConfig();
    });

    selectedCurrency.subscribe((data) => {
        if (data === null) return;

        setUserProperties(analytics, {
            currency: data,
        });

        logMetric({currency: data});

        localStorage.setItem('currency', data);
    });

    storeSection.subscribe((data) => {
        if (data == null) return;

        logMetric({screen_view: data});

        logEvent(analytics, 'screen_view', {
            firebase_screen: data,
            firebase_screen_class: 'web',
        });

        localStorage.setItem('storeSection', data);
    });

    const logout = () => {
        logMetric({button: 'logout'});

        fetch(logout_route, {
            method: 'GET',
        })
            .then(() => {
                localStorage.removeItem('user');
                localStorage.removeItem('storeSection');
                user.set(null);
            })
            .catch((error) => Rollbar.error("Logout error:" + error));
    };

    $effect(() => {
        if ($locale === null) return;

        logMetric({language: $locale});

        setUserProperties(analytics, {
            language: $locale,
        });
    });

    function redirectToDailyDeals() {
        navigateToSection('dailyDeals');
    }

    function redirectToAdventCalendar() {
        navigateToSection('adventCalendar');
    }

    function runDailyRedirect() {
        // Get today's date in a simple string format (e.g., "2023-10-15").
        const today = new Date().toISOString().slice(0, 10);
        // Check if we've already redirected today.
        if (localStorage.getItem("dailyRedirectDate") === today) {
            return; // Already run today, so do nothing.
        }
        // Otherwise, record that we're redirecting today.
        localStorage.setItem("dailyRedirectDate", today);
        // Execute your redirection after a very short delay
        setTimeout(() => {
            redirectToAdventCalendar();
        }, 1);
    }

    function checkGridChildren() {
        const gridElement = document.querySelector('.grid');
        const isEmpty = !gridElement || gridElement.children.length === 0;

        if (isEmpty
            && localStorage.getItem("storeSection") !== 'luckyDraw'
            && localStorage.getItem("storeSection") !== 'goldRush'
            && localStorage.getItem("storeSection") !== 'redeemCode'
            && localStorage.getItem("storeSection") !== 'giftFriend'
            && localStorage.getItem("storeSection") !== 'adventCalendar'
            && localStorage.getItem("storeSection") !== 'advancementRewards'
            && localStorage.getItem("storeSection") !== 'specialEvents'
            && localStorage.getItem("storeSection") !== 'webBattlePass') {
            redirectToDailyDeals();
        }
    }

    storePacks.subscribe((data) => {
        if (!data?.length) return;

        setTimeout(() => {
            if (localStorage.getItem("storeSection") === 'freeGifts' && !$subscriptions?.length && Object.keys($adventCalendarEvent).length !== 0) {
                setTimeout(() => {
                    redirectToAdventCalendar();
                }, 1);
            }
        }, 500);

        setTimeout(() => {
            checkGridChildren();
        }, 500);

        setTimeout(() => {
            if (localStorage.getItem("storeSection") === 'goldRush' && Object.keys($rechargeRewardsEvent).length === 0) {
                setTimeout(() => {
                    redirectToDailyDeals();
                }, 1);
            }
        }, 500);

        setTimeout(() => {
            if (localStorage.getItem("storeSection") === 'webBattlePass' && Object.keys($battlePassEvent).length === 0) {
                setTimeout(() => {
                    redirectToDailyDeals();
                }, 1);
            }
        }, 500);

        setTimeout(() => {
            if (localStorage.getItem("storeSection") === 'luckyDraw' && $storePacks.filter((pack) => pack.luckyDraw).length <= 0) {
                setTimeout(() => {
                    redirectToDailyDeals();
                }, 1);
            }
        }, 500);

        setTimeout(() => {
            if (localStorage.getItem("storeSection") === 'adventCalendar' && Object.keys($adventCalendarEvent).length === 0) {
                setTimeout(() => {
                    redirectToDailyDeals();
                }, 1);
            }
        }, 500);

        setTimeout(() => {
            if (localStorage.getItem("storeSection") !== 'adventCalendar' &&
                Object.keys($adventCalendarEvent).length !== 0) {
                runDailyRedirect();
            }
        }, 500);

        setTimeout(() => {
            if (localStorage.getItem("storeSection") === 'vip') {
                const vipPacks = ($storePacks || []).filter(pack => pack.storeSection === 'vip');
                const hasMatchingVipPack = vipPacks.some(pack => {
                    const matchResult = pack.bundleName.match(/lvl(\d+)pack/);
                    return matchResult !== null && matchResult[1] <= $vipLevel;
                });
                if (!hasMatchingVipPack) {
                    redirectToDailyDeals();
                }
            }
        }, 500);

        setTimeout(() => {
            if (localStorage.getItem("storeSection") === 'freeGifts' && Object.keys($gameConfig).length > 0) {
                setTimeout(() => {
                    redirectToDailyDeals();
                }, 1);
            }
        }, 500);

        if (localStorage.getItem("storeSection") !== 'promos') return;

        setTimeout(() => {
            if (data.filter((pack) => pack.storeSection === 'promos').length <= 0) {
                redirectToDailyDeals();
            }
        }, 1);
    });

    let storePack = $state(null);
    let packDetails = $state(null);

    setContext('buyPack', {buyPack});

    function buyPack(event) {
        storePack = event.detail.storePack;
        packDetails = event.detail.packDetails;
        select_payment_modal_open = true;
    }

    let select_payment_modal_open = $state(false);
    const payments_modal_show = () => (select_payment_modal_open = true);
    let payments_modal_hide = () => {
        select_payment_modal_open = false;
    };

    let system_message_modal_open = $state(false);
    const system_message_modal_show = () => (system_message_modal_open = true);
    let system_message_modal_hide = () => {
        system_message_modal_open = false;
    };

    systemMessages.subscribe((data) => {
            if (data && Object.keys(data).length === 0 && Object.getPrototypeOf(data) === Object.prototype) return;
            _systemMessages = data;

            const firstMessage = Object.values(data)[0];
            const packIdMatch = firstMessage?.link?.match(/^Pack id:(\d+)$/);
            if (packIdMatch && localStorage.getItem('presetSystemMessageId') === String(firstMessage.id)) return;

            setTimeout(() => {
                system_message_modal_show();
            }, 1000);
        }
    );

    let settings_modal_open = $state(false);
    const settings_modal_show = event => settings_modal_open = true;
    let settings_modal_hide = event => settings_modal_open = false;

    const languageNames = {
        en: 'English',
        es: 'Español',
        fr: 'Français',
        de: 'Deutsch',
        it: 'Italiano',
        ja: '日本語',
        ko: '한국어',
        pt: 'Português',
        ru: 'русский',
        zh: '中文',
        tr: 'Türkçe',
    };

    let activeSubTab = $state('subscriptions');

    function changeSubTab(tab) {
        activeSubTab = tab;
        // Update URL with the tab query parameter
        const currentPath = window.location.pathname;
        goTo(`${currentPath}?tab=${tab}`, true);
    }

    // Initialize activeSubTab from URL query parameter
    function initializeSubTab() {
        const urlParams = new URLSearchParams(window.location.search);
        const tabParam = urlParams.get('tab');

        if (tabParam && ['subscriptions', 'resources', 'gold'].includes(tabParam)) {
            activeSubTab = tabParam;
        }

        if (tabParam && ['growthCapital', 'partisanProgress', 'resourceTrove'].includes(tabParam)) {
            activeAdvancementSubTab = tabParam;
        }
        if (tabParam && ['battlePass', 'specialEvents', 'cardCollection', 'dividedNation', 'chainDeals', 'singleJackFever'].includes(tabParam)) {
            activeEventsSubTab = tabParam;
        }
    }

    let activeAdvancementSubTab = $state('growthCapital');

    let hasGrowthCapital = $derived.by(() => {
        const levels = $growthFundConfig?.levels ? Object.keys($growthFundConfig.levels) : [];
        if (levels.length === 0) return false;
        return !levels.every(l => $claimedGrowthFundPrizes.includes(parseInt(l)));
    });
    let hasPartisanProgress = $derived.by(() => {
        const levels = $monsterGrowthFundConfig?.levels ? Object.keys($monsterGrowthFundConfig.levels) : [];
        if (levels.length === 0) return false;
        return !levels.every(l => $claimedMonsterGrowthFundPrizes.includes(parseInt(l)));
    });
    let hasResourceTrove = $derived($storePacks.some(p => p.storeSection === 'resourceTrove'));
    let hasAnyAdvancementRewards = $derived(hasGrowthCapital || hasPartisanProgress || hasResourceTrove);

    function changeAdvancementSubTab(tab) {
        activeAdvancementSubTab = tab;
        const currentPath = window.location.pathname;
        goTo(`${currentPath}?tab=${tab}`, true);
    }

    // Auto-select first available advancement sub-tab, or redirect away if all empty
    $effect(() => {
        if ($storeSection !== 'advancementRewards') return;
        if ($storePacksLoading) return;
        if (!hasAnyAdvancementRewards) {
            navigateToSection('dailyDeals');
            return;
        }
        const tabAvailable =
            (activeAdvancementSubTab === 'growthCapital' && hasGrowthCapital) ||
            (activeAdvancementSubTab === 'partisanProgress' && hasPartisanProgress) ||
            (activeAdvancementSubTab === 'resourceTrove' && hasResourceTrove);
        if (!tabAvailable) {
            if (hasGrowthCapital) activeAdvancementSubTab = 'growthCapital';
            else if (hasPartisanProgress) activeAdvancementSubTab = 'partisanProgress';
            else if (hasResourceTrove) activeAdvancementSubTab = 'resourceTrove';
        }
    });

    // ── Special Events sub-tabs (Battle Pass / Special Events) ──
    let activeEventsSubTab = $state('battlePass');

    let hasBattlePassPacks = $derived(
        ($storePacks || []).some(pack =>
            pack.perks?.some(p => p.startsWith('battlePass')) &&
            !pack.isHidden &&
            !pack.hasEventPass &&
            pack.bonusRewards?.length > 0 &&
            (!pack.endAt || new Date(pack.endAt).getTime() > Date.now())
        )
    );

    let hasSpecialEventPacks = $derived(
        ($storePacks || []).some(pack =>
            pack.storeSection === 'specialEvents' &&
            !pack.perks?.some(p => p.startsWith('battlePass')) &&
            (!pack.endAt || new Date(pack.endAt).getTime() > Date.now())
        )
    );

    let hasCardCollectionPacks = $derived(
        ($storePacks || []).some(pack =>
            pack.storeSection === 'cardCollection' &&
            (!pack.endAt || new Date(pack.endAt).getTime() > Date.now())
        )
    );

    let hasDividedNationPacks = $derived(
        ($storePacks || []).some(pack =>
            pack.storeSection === 'pvpArena' &&
            (!pack.endAt || new Date(pack.endAt).getTime() > Date.now())
        )
    );

    let hasSingleJackFeverPacks = $derived(
        ($storePacks || []).some(pack =>
            pack.storeSection === 'singleJackFever' &&
            (!pack.endAt || new Date(pack.endAt).getTime() > Date.now())
        )
    );

    // Chain Deals is gated on the event itself, not on packs: its packs are
    // hidden (no storeSection) and are referenced by internalId from the ladder.
    let hasChainDeals = $derived(!!$chainPromoEvent?.id);

    function changeEventsSubTab(tab) {
        activeEventsSubTab = tab;
        const currentPath = window.location.pathname;
        goTo(`${currentPath}?tab=${tab}`, true);
        if (tab === 'cardCollection') {
            markSeenFlag('cardCollectionSeen');
        }
        if (tab === 'dividedNation') {
            markSeenFlag('dividedNationSeen');
        }
        if (tab === 'chainDeals') {
            markSeenFlag('chainDealsSeen');
        }
        if (tab === 'singleJackFever') {
            markSeenFlag('singleJackFeverSeen');
        }
    }

    // First available Events sub-tab, in the same order they render in the submenu
    // (newest event first).
    function firstAvailableEventsSubTab() {
        if (hasSingleJackFeverPacks) return 'singleJackFever';
        if (hasChainDeals) return 'chainDeals';
        if (hasDividedNationPacks) return 'dividedNation';
        if (hasCardCollectionPacks) return 'cardCollection';
        if (hasSpecialEventPacks) return 'specialEvents';
        if (hasBattlePassPacks) return 'battlePass';
        return null;
    }

    // Tracks the previous store section so we can detect navigation INTO the Events
    // tab from a different one. Intentionally a plain (non-reactive) variable.
    let prevStoreSection;

    $effect(() => {
        if ($storeSection !== 'specialEvents') {
            prevStoreSection = $storeSection;
            return;
        }
        if ($storePacksLoading) return;

        // When arriving from another section, always start on the first available
        // sub-tab instead of keeping the previous selection (e.g. Battle Pass, the
        // last tab). Selecting a sub-tab stays within this section, so it isn't reset.
        const enteredFromOtherSection =
            prevStoreSection !== undefined && prevStoreSection !== 'specialEvents';
        prevStoreSection = $storeSection;

        const tabAvailable =
            (activeEventsSubTab === 'battlePass' && hasBattlePassPacks) ||
            (activeEventsSubTab === 'specialEvents' && hasSpecialEventPacks) ||
            (activeEventsSubTab === 'cardCollection' && hasCardCollectionPacks) ||
            (activeEventsSubTab === 'dividedNation' && hasDividedNationPacks) ||
            (activeEventsSubTab === 'chainDeals' && hasChainDeals) ||
            (activeEventsSubTab === 'singleJackFever' && hasSingleJackFeverPacks);

        if (enteredFromOtherSection || !tabAvailable) {
            const firstTab = firstAvailableEventsSubTab();
            if (firstTab) activeEventsSubTab = firstTab;
        }
    });

    // Sections that should be checked for pack expiration
    const sectionsWithExpiration = [
        'promos', 'dailyDeals', 'beginnerPacks', 'welcomeBackPacks',
        'midweekMadness', 'webshopExclusive', 'specialEvents', 'customizablePacks'
    ];

    let expirationTimeout = null;

    function scheduleExpirationCheck() {
        // Clear any existing timeout
        if (expirationTimeout) {
            clearTimeout(expirationTimeout);
            expirationTimeout = null;
        }

        const currentSection = localStorage.getItem("storeSection");
        if (!sectionsWithExpiration.includes(currentSection)) return;

        const now = Date.now();

        // Events tab hosts 'specialEvents', 'cardCollection', 'pvpArena' and 'singleJackFever' packs as sub-tabs
        const isPackInSection = (pack) =>
            pack.storeSection === currentSection ||
            (currentSection === 'specialEvents' && pack.storeSection === 'cardCollection') ||
            (currentSection === 'specialEvents' && pack.storeSection === 'pvpArena') ||
            (currentSection === 'specialEvents' && pack.storeSection === 'singleJackFever');

        // Get all packs in the current section that have an endAt
        const packsWithExpiration = $storePacks.filter(pack =>
            isPackInSection(pack) &&
            pack.endAt &&
            new Date(pack.endAt).getTime() > now
        );

        if (packsWithExpiration.length === 0) {
            // No packs with future expiration - check if section is empty.
            // Chain Deals lives on the Events tab but its packs are hidden (no
            // storeSection), so the active event must keep the tab alive.
            const activePacks = $storePacks.filter(pack =>
                isPackInSection(pack) &&
                (!pack.endAt || new Date(pack.endAt).getTime() > now)
            );
            if (activePacks.length === 0 && !(currentSection === 'specialEvents' && hasChainDeals)) {
                redirectToDailyDeals();
            }
            return;
        }

        // Find the earliest endAt
        const earliestEndAt = Math.min(
            ...packsWithExpiration.map(pack => new Date(pack.endAt).getTime())
        );

        // Schedule check 1 second after the earliest expiration
        const delay = earliestEndAt - now + 1000;
        expirationTimeout = setTimeout(() => {
            const activePacks = $storePacks.filter(pack =>
                isPackInSection(pack) &&
                (!pack.endAt || new Date(pack.endAt).getTime() > Date.now())
            );

            if (activePacks.length === 0 && !(currentSection === 'specialEvents' && hasChainDeals)) {
                redirectToDailyDeals();
            } else {
                // Schedule the next expiration check
                scheduleExpirationCheck();
            }
        }, delay);
    }

    // Re-schedule when section changes
    $effect(() => {
        if ($storeSection && $storePacks.length > 0) {
            scheduleExpirationCheck();
        }
    });

    onMount(() => {
        initializeSubTab();

        // Listen for popstate events to update subtab when user navigates
        const handlePopState = () => {
            initializeSubTab();
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
            if (expirationTimeout) {
                clearTimeout(expirationTimeout);
            }
        };
    });
</script>

{#if user && _user}
    {#if $isLoading}
        <Loader/>
    {:else}
        <Notifications/>
        {#await getPacks}
            <Loader/>
        {:then value}
            {#if $storePacksLoading}
                <Loader/>
            {:else if $storePacksError}
                <StoreError />
            {:else if Object.keys($gameConfig).length === 0}
                <Loader/>
            {:else}
            <VanishingHeader offset={50} tolerance={10}>
                <div class="user-area" class:menu-open={$userMenuOpen}>
                    <h1>
                        {$_('OnboardingStep1Text1_2019')}
                    </h1>
                    <button type="button" class="user-burger" aria-label="Menu" aria-expanded={$userMenuOpen} onclick={() => userMenuOpen.set(!$userMenuOpen)}>
                        <Fa icon={$userMenuOpen ? faXmark : faBars} fw primaryColor={iconColor}/>
                    </button>
                    <div class="user-controls themed">
                        <div class="user-select">
                            <Fa icon={faGlobe} fw primaryColor={iconColor}/>
                            <select bind:value={$locale} class="selector">
                                {#each $locales as locale}
                                    <option value={locale} title={languageNames[locale]}
                                    >{locale.toUpperCase()}</option
                                    >
                                {/each}
                            </select>
                        </div>

                        <!--                        <div class="user-select">-->
                        <!--                            <a title="{$_('Settings')}" on:click={settings_modal_show} href="javascript:">-->
                        <!--                                <Fa icon={faGear} fw primaryColor="#gold"/>-->
                        <!--                            </a>-->
                        <!--                        </div>-->
                    </div>
                    <div class="user-info">
                        <b>
                            <Fa icon={faUser} fw primaryColor={iconColor}/>{_user?.user.username}
                        </b>
                        <a onclick={logout} href="#logout" class="">{$_('Logout')}</a>
                    </div>
                </div>
                {#if $isMobile}
                    <MobileTabBar/>
                {:else}
                    <ScrollableNavbar/>
                {/if}
            </VanishingHeader>
            {#if $storeSection !== "redeemCode" && $storeSection !== "giftFriend" && $storeSection !== "adventCalendar"}
                {#if $storeSection === 'goldAndResources'}
                    <div class="tabs submenu" use:dragScroll>
                        <button type="button"
                                class:active={activeSubTab === 'subscriptions'}
                                onclick={() => changeSubTab('subscriptions')}>
                            {$_('NewShopTabSubscriptions')}
                            {#if subTabInterest.subscriptions?.dot}<NotificationDot kind={subTabInterest.subscriptions.kind} />{/if}
                        </button>
                        <button type="button"
                                class:active={activeSubTab === 'resources'}
                                onclick={() => changeSubTab('resources')}>
                            {$_('NewShopTabResources')}
                            {#if subTabInterest.resources?.dot}<NotificationDot kind={subTabInterest.resources.kind} />{/if}
                        </button>
                        <button type="button"
                                class:active={activeSubTab === 'gold'}
                                onclick={() => changeSubTab('gold')}>
                            {$_('NewShopTabGoldPacks')}
                        </button>
                    </div>
                {/if}
                {#if $storeSection === 'advancementRewards' && [hasGrowthCapital, hasPartisanProgress, hasResourceTrove].filter(Boolean).length > 1}
                    <div class="tabs submenu" use:dragScroll>
                        {#if hasGrowthCapital}
                            <button type="button"
                                    class:active={activeAdvancementSubTab === 'growthCapital'}
                                    onclick={() => changeAdvancementSubTab('growthCapital')}>
                                {$_('CityGrowthPack20Title')}
                                {#if subTabInterest.growthCapital?.dot}<NotificationDot kind={subTabInterest.growthCapital.kind} />{/if}
                            </button>
                        {/if}
                        {#if hasPartisanProgress}
                            <button type="button"
                                    class:active={activeAdvancementSubTab === 'partisanProgress'}
                                    onclick={() => changeAdvancementSubTab('partisanProgress')}>
                                {$_('MonsterProgressPack20Title')}
                                {#if subTabInterest.partisanProgress?.dot}<NotificationDot kind={subTabInterest.partisanProgress.kind} />{/if}
                            </button>
                        {/if}
                        {#if hasResourceTrove}
                            <button type="button"
                                    class:active={activeAdvancementSubTab === 'resourceTrove'}
                                    onclick={() => changeAdvancementSubTab('resourceTrove')}>
                                {$_('resourceTroveTitle')}
                            </button>
                        {/if}
                    </div>
                {/if}
                {#if $storeSection === 'specialEvents' && [hasBattlePassPacks, hasSpecialEventPacks, hasCardCollectionPacks, hasDividedNationPacks, hasChainDeals, hasSingleJackFeverPacks].filter(Boolean).length > 1}
                    <div class="tabs submenu" use:dragScroll>
                        {#if hasSingleJackFeverPacks}
                            <button type="button"
                                    class:active={activeEventsSubTab === 'singleJackFever'}
                                    onclick={() => changeEventsSubTab('singleJackFever')}>
                                {$_('SingleJackFeverEventTitle')}
                                {#if localStorage.getItem("singleJackFeverSeen") !== "true"}
                                    <Ribbon>{$_('New')}</Ribbon>
                                {/if}
                            </button>
                        {/if}
                        {#if hasChainDeals}
                            <button type="button"
                                    class:active={activeEventsSubTab === 'chainDeals'}
                                    onclick={() => changeEventsSubTab('chainDeals')}>
                                {$_('ChainDealTitle')}
                                {#if localStorage.getItem("chainDealsSeen") !== "true"}
                                    <Ribbon>{$_('New')}</Ribbon>
                                {/if}
                            </button>
                        {/if}
                        {#if hasDividedNationPacks}
                            <button type="button"
                                    class:active={activeEventsSubTab === 'dividedNation'}
                                    onclick={() => changeEventsSubTab('dividedNation')}>
                                {$_('DividedNationShopTab', {default: 'The Divided Nation'})}
                                {#if localStorage.getItem("dividedNationSeen") !== "true"}
                                    <Ribbon>{$_('New')}</Ribbon>
                                {/if}
                            </button>
                        {/if}
                        {#if hasCardCollectionPacks}
                            <button type="button"
                                    class:active={activeEventsSubTab === 'cardCollection'}
                                    onclick={() => changeEventsSubTab('cardCollection')}>
                                {$_('CardCollectionGenericEventTitle')}
                                {#if localStorage.getItem("cardCollectionSeen") !== "true"}
                                    <Ribbon>{$_('New')}</Ribbon>
                                {/if}
                            </button>
                        {/if}
                        {#if hasSpecialEventPacks}
                            <button type="button"
                                    class:active={activeEventsSubTab === 'specialEvents'}
                                    onclick={() => changeEventsSubTab('specialEvents')}>
                                {$_('SpecialCategoryRecommendedQuest')} {$_('EventsWindowTitle')}
                            </button>
                        {/if}
                        {#if hasBattlePassPacks}
                            <button type="button"
                                    class:active={activeEventsSubTab === 'battlePass'}
                                    onclick={() => changeEventsSubTab('battlePass')}>
                                {$_('BattlePassTitle') === 'BattlePassTitle' ? $_('BattlePassShopTabTitle') : $_('BattlePassTitle')}
                            </button>
                        {/if}
                    </div>
                {/if}
                {#if Object.keys($gameConfig).length > 0 && $storeSection !== 'freeGifts' && $storeSection !== 'advancementRewards' && $storeSection !== 'webBattlePass'}
                    <div class="discount-banner {discountBannerClass}">
                        <h3 class="animate__animated animate__shakeY animate__delay-2s">Up to
                            <span>{$maxWebshopDiscount}%</span>
                            discount in this
                            shop!
                        </h3>
                        <h4 class=""><span>{$maxWebshopDiscount}%</span>
                            <strong>discount</strong></h4>
                    </div>
                {/if}
            {/if}
            <GravityMalfunction isStore={$storeSection !== 'freeGifts' && $storeSection !== 'adventCalendar'} />
            <section class="pack-listing">
                {#if $storeSection === 'promos'}
                    {#if ($storePacks || []).filter((pack) => pack.storeSection === 'promos').length > 0}
                        <Promos/>
                    {/if}
                {:else if $storeSection === 'customizablePacks'}
                    <CustomizablePacks/>
                {:else if $storeSection === 'dailyDeals'}
                    <DailyDeals/>
                {:else if $storeSection === 'goldAndResources'}
                    {#if activeSubTab === 'subscriptions'}
                        {#if ($storePacks || []).filter((pack) => pack.storeSection === 'subscriptions').length > 0 || $subscriptions?.length > 0}
                            <Subscriptions/>
                        {/if}
                    {:else if activeSubTab === 'resources'}
                        <Resources/>
                    {:else if activeSubTab === 'gold'}
                        <Gold/>
                    {/if}
                {:else if $storeSection === 'goldRush' && Object.keys($rechargeRewardsEvent).length !== 0}
                    <GoldRush/>
                {:else if $storeSection === 'giftFriend'}
                    <GiftFriend/>
                {:else if $storeSection === 'redeemCode'}
                    <RedeemCode/>
                {:else if $storeSection === 'luckyDraw' && Object.keys($luckyDrawEvent).length !== 0}
                    <LuckyDraw/>
                {:else if $storeSection === 'beginnerPacks'}
                    <BeginnerPacks/>
                {:else if $storeSection === 'welcomeBackPacks'}
                    <WelcomeBackPacks/>
                {:else if $storeSection === 'midweekMadness'}
                    <MidweekMadness/>
                {:else if $storeSection === 'vip'}
                    <Vip/>
                {:else if $storeSection === 'advancementRewards'}
                    {#if activeAdvancementSubTab === 'growthCapital'}
                        <GrowthCapital/>
                    {:else if activeAdvancementSubTab === 'partisanProgress'}
                        <PartisanProgress/>
                    {:else if activeAdvancementSubTab === 'resourceTrove'}
                        <ResourceTrove/>
                    {/if}
                {:else if $storeSection === 'featured'}
                    <Featured/>
                {:else if $storeSection === 'webshopExclusive'}
                    <WebshopExclusive/>
                {:else if $storeSection === 'adventCalendar' && Object.keys($adventCalendarEvent).length !== 0}
                    <AdventCalendar/>
                {:else if $storeSection === 'freeGifts'}
                    <FreeGifts/>
                {:else if $storeSection === 'webBattlePass' && Object.keys($battlePassEvent).length !== 0}
                    <WebBattlePass/>
                {:else if $storeSection === 'specialEvents'}
                    {#if activeEventsSubTab === 'battlePass' && hasBattlePassPacks}
                        <BattlePass/>
                    {:else if activeEventsSubTab === 'cardCollection' && hasCardCollectionPacks}
                        <CardCollection/>
                    {:else if activeEventsSubTab === 'dividedNation' && hasDividedNationPacks}
                        <DividedNation/>
                    {:else if activeEventsSubTab === 'chainDeals' && hasChainDeals}
                        <ChainDeals/>
                    {:else if activeEventsSubTab === 'singleJackFever' && hasSingleJackFeverPacks}
                        <SingleJackFever/>
                    {:else}
                        <SpecialEvents/>
                    {/if}
                {/if}
            </section>
            {/if}
        {:catch error}
            <StoreError />
        {/await}
    {/if}
    <Modal class="checkout" bind:open={select_payment_modal_open}>
        <Checkout {storePack} {packDetails} {payments_modal_hide}/>
    </Modal>
    <Modal class="system-message" bind:open={system_message_modal_open}>
        <SystemMessage {_systemMessages} {system_message_modal_hide}/>
    </Modal>
    <Modal class="settings-modal" bind:open={settings_modal_open}>
        <Settings {settings_modal_hide}/>
    </Modal>
{/if}

<style lang="scss">
  @use 'sass:color';
  @use './styles/vars.scss' as *;
  @use './styles/breakpoints.scss' as bp;
  @use './styles/nav-pills.scss' as pills;

  :global {
    .tabs.submenu {
      overflow: visible;

      .ribbon-wrapper {
        width: 75px;
        height: 75px;
        transform: scale(0.5);
        transform-origin: top left;
        left: 0;
        top: -5px;
        pointer-events: none;

        span {
          transform: rotate(-45deg);
          left: -21px;
          top: 19px;
          line-height: 20px;
          width: 100px;
        }
      }
    }

    .pin, .unpin {
      &.header {
        height: $headerHeight;
        z-index: 50;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .selectContainer {
        width: 80px;
      }

      .tabs .lock-icon {
        margin-right: 5px;
      }

      .tabs .ribbon-wrapper {
        width: 23px;
        height: 41px;
        transform: scale(0.8);
        left: 0;
        top: -21px;

        span {
          transform: rotate(0deg);
          left: -38px;
          line-height: 17px;
          width: 92px;
        }
      }
    }
  }

  .discount-banner {
    --banner-accent: #f6ff0080;
    --banner-main: rgb(188 38 235);
    --banner-main-darker: #152998;
    --banner-dark: #ca4afa;
    --border-width: 5px;
    height: 50px;
    width: auto;
    background: var(--banner-main);
    background: linear-gradient(145deg, var(--banner-main) 0%, var(--banner-main-darker) 100%);
    display: table;
    border-radius: var(--border-width);
    position: relative;
    box-shadow: 0 0 25px var(--banner-dark), 0 0 30px rgba(0, 0, 0, 0.2) inset;
    padding: 0 20px;
    margin: 0 auto;
    top: 135px;
    cursor: default;

    @include bp.mobile {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 90%;
      margin: 0 auto;
      padding: 8px 16px;
      height: auto;
      min-height: 46px;
      border-radius: 10px;
      top: bp.$m-header + 20; // 80px: 20px below the header (no submenu) / below the submenu
      box-shadow: 0 0 16px var(--banner-dark);
    }

    &.bf {
      &::after {
        position: absolute;
        content: "";
        top: calc(-1 * var(--border-width));
        left: calc(-1 * var(--border-width));
        z-index: -1;
        width: calc(100% + var(--border-width) * 2);
        height: calc(100% + var(--border-width) * 2);
        background: linear-gradient(
                        60deg,
                        hsl(0, 0%, 0%),
                        hsl(353, 94%, 40%),
                        hsl(0, 0%, 0%),
                        hsl(353, 94%, 40%),
                        hsl(0, 0%, 0%),
                        hsl(353, 94%, 40%),
                        hsl(0, 0%, 0%),
                        hsl(353, 94%, 40%)
        );
        background-size: 300% 300%;
        background-position: 0 50%;
        border-radius: calc(2 * var(--border-width));
        animation: moveGradient 4s alternate infinite;
        filter: blur(1px);
      }

      @keyframes moveGradient {
        50% {
          background-position: 100% 50%;
        }
      }
    }

    &.winter {
      &::after {
        position: absolute;
        content: "";
        top: calc(-1 * var(--border-width));
        left: calc(-1 * var(--border-width));
        z-index: -1;
        width: calc(100% + var(--border-width) * 2);
        height: calc(100% + var(--border-width) * 2);
        background: linear-gradient(
                        60deg,
                        hsl(0, 0%, 0%),
                        hsl(180, 78%, 58%),
                        hsl(0, 0%, 0%),
                        hsl(180, 78%, 58%),
                        hsl(0, 0%, 0%),
                        hsl(180, 78%, 58%),
                        hsl(0, 0%, 0%),
                        hsl(180, 78%, 58%)
        );
        background-size: 300% 300%;
        background-position: 0 50%;
        border-radius: calc(2 * var(--border-width));
        animation: moveGradient 5s alternate infinite;
        filter: blur(1px);
      }

      @keyframes moveGradient {
        50% {
          background-position: 100% 50%;
        }
      }
    }

    &::after {
      position: absolute;
      content: "";
      top: calc(-1 * var(--border-width));
      left: calc(-1 * var(--border-width));
      z-index: -1;
      width: calc(100% + var(--border-width) * 2);
      height: calc(100% + var(--border-width) * 2);
      background: linear-gradient(
                      60deg,
                      hsl(224, 85%, 66%),
                      hsl(269, 85%, 66%),
                      hsl(314, 85%, 66%),
                      hsl(359, 85%, 66%),
                      hsl(44, 85%, 66%),
                      hsl(89, 85%, 66%),
                      hsl(134, 85%, 66%),
                      hsl(179, 85%, 66%)
      );
      background-size: 300% 300%;
      background-position: 0 50%;
      border-radius: calc(2 * var(--border-width));
      animation: moveGradient 4s alternate infinite;
      filter: blur(1px);
    }

    @keyframes moveGradient {
      50% {
        background-position: 100% 50%;
      }
    }

    h3 {
      font-family: var(--font-family-sans-condensed);
      font-weight: bold;
      font-size: 3rem;
      font-variant: small-caps;
      padding-left: 35px;
      text-shadow: 4px 3px 0 rgba(0, 0, 0, 0.1);
      line-height: 50px;
      color: aliceblue;
      margin: 0;
      white-space: nowrap;

      @media (max-width: 700px) {
        font-size: 1.5rem;
        line-height: 35px;
      }

      @media (max-width: 300px) {
        font-size: 1rem;
      }

      span {
        color: yellow;
      }
    }


    h4 {
      background: rgb(251, 244, 63);
      background: linear-gradient(146deg, rgba(251, 244, 63, 1) 0%, rgba(252, 117, 70, 1) 100%);
      border-radius: 50%;
      display: inline-block;
      height: 86px;
      width: 86px;
      position: absolute;
      text-align: center;
      margin: 0;
      font-weight: 700;
      font-family: var(--font-family-sans-condensed);
      line-height: 22px;
      padding-top: 22px;
      letter-spacing: -0.5px;
      overflow: hidden;
      top: -18px;
      left: -40px;
      //outline: 5px solid var(--banner-dark);
      box-shadow: 0 4px 0 #301806, 0 0 20px rgba(0, 0, 0, 0.4), 0 0 30px rgba(0, 0, 0, 0.2) inset;
      transform: rotate(-15deg);

      @media (max-width: 700px) {
        transform: rotate(-15deg) scale(0.6);
        top: -25px;
        left: -29px;
      }

      span {
        font-size: 4rem;
        color: #4a2408;
        text-shadow: 0 2px 0 rgb(255 255 255 / 30%);
      }

      strong {
        text-transform: uppercase;
        font-size: 1.4rem;
        top: -1px;
        position: relative;
        background-color: rgba(0, 0, 0, 0.2);
        text-shadow: 0 1px 0 rgba(0, 0, 0, 0.2);
        display: block;
        line-height: 3rem;
        color: #f6f8c2;
        padding-bottom: 20px;
      }
    }

    // Mobile overrides must come AFTER the base h3/h4 rules above: a media query
    // adds no specificity, so an earlier @include would lose on source order.
    @include bp.mobile {
      // The standalone % circle is redundant with the inline "Up to X%" text on a
      // full-width strip — hide it so the message centers cleanly.
      h4 {
        display: none;
      }

      h3 {
        padding-left: 0;
        text-align: center;
        white-space: normal; // allow wrapping on the narrower 90% banner
        font-size: 2.25rem; // ~1.5x the previous mobile size
        line-height: 1.15;
      }
    }
  }

  .themed {
    --borderRadius: 5px;
    --height: 35px;
    --border: none !important;
    --margin: 0 10px 0 0;
  }

  // Mobile burger: collapse the user controls into a single icon that expands
  // them in place (to the left of the burger). Desktop never shows the burger,
  // so its user-area layout is unchanged.
  .user-burger {
    display: none;
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 6px 8px;
    font-size: 20px;
    line-height: 1;
    align-items: center;
    justify-content: center;
    -webkit-tap-highlight-color: transparent;

    // Spin the icon when it morphs between ☰ and ✕.
    :global(svg) {
      transition: transform 0.25s ease;
    }
  }

  .user-area.menu-open .user-burger :global(svg) {
    transform: rotate(90deg);
  }

  @include bp.mobile {
    .user-burger {
      display: flex;
      order: 3; // keep the burger pinned to the right; controls expand to its left
    }

    // Collapsed: hide the controls until the burger opens the menu. `:not(.menu-open)`
    // raises specificity above the base `.user-area .user-controls { display:flex }`.
    .user-area:not(.menu-open) .user-controls,
    .user-area:not(.menu-open) .user-info {
      display: none;
    }

    .user-area.menu-open .user-controls {
      order: 1;
    }
    .user-area.menu-open .user-info {
      order: 2;
    }
  }

  // The fixed header is a global flex `.header`. Scoped via :has(> .user-area)
  // so it only matches this header — never StorePack's own `.header` art band.
  // Combined: the user-area is overlaid (absolute) so the header collapses to the
  // tab-bar height. Stacked: the user row sits above the MobileTabBar.
  @include bp.mobile {
    @if bp.$combine-mobile-header {
      :global(.header:has(> .user-area)) {
        height: auto; // user-area is out of flow; header shrinks to the tab bar
      }
    } @else {
      :global(.header:has(> .user-area)) {
        flex-direction: column;
        align-items: stretch;
        height: auto;
      }
    }

    // Keep the header pinned on mobile so the sticky submenu below it never has a
    // bare gap above it — the whole nav (menu + submenu) stays fixed on top.
    :global(.header.unpin) {
      transform: translateY(0) !important;
    }
  }

  // Sub-tabs use the exact same pill style + behaviour as the main MobileTabBar.
  @include bp.mobile {
    .tabs.submenu {
      @include pills.pill-row;
      // Tighter than the default pill-row so the strip stays a slim 40px band.
      padding: 6px 12px;
      height: 40px;
      // Stick directly beneath the fixed header so the submenu stays fixed on top
      // together with the main menu instead of scrolling away. `top: $m-header`
      // reproduces the previous resting position (the old `bottom: -$m-header`).
      position: sticky;
      top: bp.$m-header;
      bottom: auto;
      z-index: 9;

      button {
        @include pills.pill;
        // Let the sub-tabs fit the 40px strip instead of bulging out of it.
        min-height: 0;

        // The slim pills have less padding than the main tabs, so the corner
        // notification dot sat on the label's last letters — overhang the pill
        // corner instead (the strip has room around each pill).
        :global(.notif-dot) {
          top: -3px;
          right: -3px;
        }

        &.active {
          @include pills.pill-active;
        }
      }
    }
  }

  .pack-listing {
    margin-top: $headerHeight + 60;
    min-height: 57vh;

    // Mobile shell uses a single offset across the whole <=768 range
    // (supersedes the old 700px-only rule, now removed).
    // = banner shift ($m-header + 20) + a ~10px gap, so the listing clears the
    // banner's bottom regardless of how many lines the banner wraps to.
    @include bp.mobile {
      margin-top: bp.$m-header + 30;
    }
  }

  .user-info {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: center;
    line-height: 18px;
    border-left: 1px solid rgba(255, 255, 255, 0.2);
    padding-left: 10px;
    white-space: nowrap;
    overflow: hidden;
    text-align: right;

    b {
      min-width: 80px;
    }
  }

  .user-area {
    height: 50px;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    padding: 0 20px;
    display: flex;
    background: color.adjust($dark-color, $lightness: -1%);
    align-content: center;
    align-items: center;
    background: linear-gradient(180deg, $dark-color 0%, #000 100%);
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.4);
    justify-content: space-between;

    @include bp.mobile {
      @if bp.$combine-mobile-header {
        // Overlay the user controls on the right of the tab bar. A left-to-right
        // fade (transparent → nav brown) lets the tab pills appear to slide under
        // it; the strip gets right padding so any pill can be scrolled into view.
        position: absolute;
        top: 0;
        right: 0;
        left: auto; // release the base `left: 0` so the box shrinks to its content
        height: bp.$m-header;
        width: auto;
        min-height: 0;
        // Tight padding/gap so the controls take as little width as possible,
        // leaving more room for the menu pills underneath.
        padding: 0 6px 0 22px; // left padding holds the fade run-in; controls sit on solid
        z-index: 5;
        align-items: center;
        justify-content: flex-end;
        gap: 6px;
        // Solid nav-brown behind the controls (so the language selector never
        // shows a tab pill through it), with only a short fade run-in on the
        // left edge to blend into the pills that scroll underneath.
        background: linear-gradient(90deg, rgba(81, 27, 11, 0) 0, #511b0b 22px);
      } @else {
        // Flow in the header instead of overlapping the tab bar below it.
        position: relative;
        height: auto;
        min-height: 44px;
        padding: calc(4px + #{bp.$safe-top}) 12px 4px;
      }

      // Drop the inset top shadow on mobile (desktop keeps it) so the overlaid
      // user controls blend flat into the nav bar.
      box-shadow: none;

      // Hide the desktop shop title across the whole mobile range
      // (supersedes the old 700px-only rule, now removed).
      h1 {
        display: none;
      }

      .user-controls {
        margin-left: 0;
      }

      .user-info {
        font-size: 12px;
        padding-left: 6px;

        b {
          min-width: 0;
        }
      }
    }

    h1 {
      margin: 0;
      padding: 0;
      font-size: 16px;
      color: $light-color;
      text-shadow: 0 1px 0 rgba(0, 0, 0, 0.6);
    }

    .user-controls {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-left: auto;

      @media (max-width: 700px) {
        margin-left: 0;
      }

      .user-select {
        position: relative;
        top: 0;
        right: 0;
        margin-right: 10px;

        // Drop the right margin on mobile so the overlaid control cluster is as
        // narrow as possible (frees menu room). Collocated here to win over the
        // base 10px rule by specificity + source order.
        @include bp.mobile {
          margin-right: 0;
        }
      }
    }
  }

  .tabs-dropdown {
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.2);
    color: #fff;
    font-family: var(--font-family-sans);
    background: $dark-color;
    text-align: center;
    font-weight: 700;
    border-radius: 5px;
    background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIj8+Cjxzdmcgdmlld0JveD0iMCAwIDI5Mi40IDI5Mi40IiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnN2Zz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgogPGcgY2xhc3M9ImxheWVyIj4KICA8dGl0bGU+TGF5ZXIgMTwvdGl0bGU+CiAgPHBhdGggZD0ibTI4Nyw2OS40YTE3LjYsMTcuNiAwIDAgMCAtMTMsLTUuNGwtMjU1LjYsMGMtNSwwIC05LjMsMS44IC0xMi45LDUuNGExNy42LDE3LjYgMCAwIDAgLTUuNSwxMi44YzAsNSAxLjgsOS4zIDUuNCwxMi45bDEyOCwxMjcuOWMzLjYsMy42IDcuOCw1LjQgMTIuOCw1LjRzOS4yLC0xLjggMTIuOCwtNS40bDEyOCwtMTI4YzMuNSwtMy41IDUuNCwtNy44IDUuNCwtMTIuOGMwLC01IC0xLjksLTkuMiAtNS41LC0xMi44bDAuMSwweiIgZmlsbD0iI2ZmZmZmZiIgaWQ9InN2Z18xIi8+CiA8L2c+Cjwvc3ZnPg==');
    padding: 0px 30px 0px 25px;
    margin: 5px 3px;
    background-repeat: no-repeat, repeat;
    background-position: right 20px top 50%, 0 0;
    background-size: 1em auto, 100%;

    @media (max-width: 768px) {
      display: block;
    }
  }
</style>
